import type { AuthToken } from './GCPAuthService';
import { currentDate } from '@/shared/utils/dateUtils';

const TOKEN_EXPIRY_BUFFER = 5 * 60 * 1000; // 5 minutes buffer for token expiry

/**
 * TokenStorage manages user/session metadata in memory.
 * The actual auth token is stored as an HttpOnly cookie by the backend
 * and is never accessible via JavaScript.
 */
export class TokenStorage {
  private memoryToken: AuthToken | null = null;

  setToken(token: AuthToken): void {
    this.memoryToken = token;
  }

  getToken(): AuthToken | null {
    if (this.memoryToken) {
      if (this.isTokenValid(this.memoryToken)) {
        return this.memoryToken;
      } else {
        this.clearToken();
        return null;
      }
    }
    return null;
  }

  clearToken(): void {
    this.memoryToken = null;
  }

  private isTokenValid(token: AuthToken): boolean {
    if (!token) return false;

    if (token.expiresAt) {
      const expiryDate = new Date(token.expiresAt);
      const now = currentDate();
      const bufferTime = new Date(now.getTime() + TOKEN_EXPIRY_BUFFER);

      if (expiryDate <= bufferTime) {
        return false;
      }
    }

    const createdAt = new Date(token.createdAt);
    const now = currentDate();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    if (createdAt < oneWeekAgo) {
      return false;
    }

    return true;
  }

  shouldRefreshToken(): boolean {
    const token = this.getToken();
    if (!token) return false;

    if (token.expiresAt) {
      const expiryDate = new Date(token.expiresAt);
      const now = currentDate();
      const refreshThreshold = new Date(now.getTime() + 15 * 60 * 1000);

      return expiryDate <= refreshThreshold;
    }

    const createdAt = new Date(token.createdAt);
    const now = currentDate();
    const sixDaysAgo = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);

    return createdAt < sixDaysAgo;
  }

  updateTokenExpiry(expiresAt: string): void {
    const token = this.getToken();
    if (token) {
      token.expiresAt = expiresAt;
      this.setToken(token);
    }
  }

  hasToken(): boolean {
    return this.getToken() !== null;
  }

  getTokenString(): string | null {
    // Token is in HttpOnly cookie — not accessible via JavaScript
    return null;
  }

  getUser(): AuthToken['user'] | null {
    const token = this.getToken();
    return token ? token.user : null;
  }
}
